// Pure parser for inbound Deepgram WS string frames. Extracted from
// `deepgramStreamingEngine.ts` so the parse rules are unit-testable
// without an open WebSocket, and so the engine class stays under
// file-size hygiene after Phase 1 lands.
//
// The engine only acts on FINAL `Results` frames today (interim results
// are ignored upstream by `interim_results=true` + this `is_final` gate
// — we let Deepgram smart-format consolidate within an utterance).

export type ParsedDeepgramResult = {
  /** Trimmed transcript text from the first alternative. Never empty when returned. */
  text: string;
  /** Always true when we return a parsed result (the engine only consumes finals). */
  isFinal: true;
};

/**
 * Returns a parsed result when the payload is a final `Results` frame
 * carrying a non-empty transcript; null otherwise (interim frames,
 * non-Results frames, malformed JSON, empty transcripts).
 *
 * Never throws — JSON.parse errors are swallowed so the engine's
 * onmessage handler stays a one-liner.
 */
export function parseDeepgramResultsFrame(raw: string): ParsedDeepgramResult | null {
  let msg: unknown;
  try {
    msg = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!msg || typeof msg !== "object") return null;
  const obj = msg as {
    type?: unknown;
    is_final?: unknown;
    channel?: { alternatives?: Array<{ transcript?: unknown }> };
  };
  if (obj.type !== "Results") return null;
  if (obj.is_final !== true) return null;
  const alt = obj.channel?.alternatives?.[0];
  const raw_text = typeof alt?.transcript === "string" ? alt.transcript : "";
  const text = raw_text.trim();
  if (!text) return null;
  return { text, isFinal: true };
}
